import { API_VERSION } from '@omni/api'
import { createPluginContext } from './context'
import {
  HOST_PROVIDED,
  findCycle,
  resolveInstallClosure,
  topoLayers,
  transitiveDependents,
} from './dep-resolver'
import { EventPipeline } from './event-pipeline'
import { createHold } from './hold.js'
import { IDLE_BATCH, firstInput, whenIdle } from './idle.js'
import { waitPatiently, yieldToTasks } from './pacing.js'
import { eagerIds, needsOf } from './lazy.js'
import { loadPluginModule } from './plugin-loader'
import { PluginStore } from './plugin-store'
import { satisfies } from './semver'
import { ServiceRegistry } from './service-registry'
import { SlotRegistry } from './slot-registry'
import { staleIds } from './stale'

const ACTIVATE_TIMEOUT_MS = 15_000
// A code load is waited for in steps (a 'plugin load slow' row each) before it fails; see pacing.js.
const LOAD_STEP_MS = 15_000
const LOAD_STEPS = 4

function isPack(manifest) {
  return manifest.kind === 'pack'
}

// The micro-kernel. Owns the input pipeline, service registry, UI slots and
// the plugin lifecycle. Everything the user sees comes from plugins.
export class Kernel {
  events = new EventPipeline()
  services = new ServiceRegistry()
  slots = new SlotRegistry()
  store = new PluginStore()

  registry = [] // manifests from registry.json
  registryError = null
  runtime = new Map() // id -> { status, reason?, plugin?, ctxHandle?, styleEls? }
  #listeners = new Set()
  #booted = false
  #woken = new Set()
  #dormant = new Map()
  #hold = createHold()
  #early = [] // dev-log rows written before omni.dev-logs is up

  constructor() {
    this.services.whenAvailable('omni.dev-logs', (log) => {
      const ch = log.channel('kernel')
      for (const [stage, data] of this.#early.splice(0)) ch.trace(stage, data)
    })
    // when a camera service appears, pointer events gain world coordinates
    this.services.whenAvailable('omni.camera', (camera) => {
      // Every pane needs its own screen -> world converter, because each pane
      // has its own camera and its own origin. Without one, `ev.world` is
      // undefined and the first tool that reads it throws.
      let panesSub = null
      const registerAll = () => {
        const panes = this.services.getNow('omni.panes')
        const ids = panes?.list?.() ?? ['main']
        for (const paneId of ids) {
          // camera.forPane arrives with the per-pane cameras; until then the
          // one camera serves the one pane, which is the same thing.
          const cameraOf = () =>
            typeof camera.forPane === 'function' ? camera.forPane(paneId) : camera
          this.events.setWorldConverter((p) => cameraOf().screenToWorld(p), paneId)
        }
      }
      registerAll()
      this.services.whenAvailable('omni.panes', (panes) => {
        registerAll()
        // one subscription only - re-running whenAvailable must not stack them
        panesSub?.dispose?.()
        panesSub = panes.subscribe?.(registerAll) ?? null
      })
    })
  }

  subscribe(cb) {
    this.#listeners.add(cb)
    return { dispose: () => this.#listeners.delete(cb) }
  }

  #notify() {
    this.#snapshot = null
    for (const cb of this.#listeners) cb()
  }

  #snapshot = null
  // stable snapshot for useSyncExternalStore
  getState() {
    if (!this.#snapshot) {
      this.#snapshot = {
        registry: this.registry,
        registryError: this.registryError,
        installed: { ...this.store.all() },
        runtime: new Map(this.runtime),
        dormant: this.dormant(),
      }
    }
    return this.#snapshot
  }

  status(id) {
    return this.runtime.get(id)?.status ?? 'inactive'
  }

  // beforeActivate(store) runs after the registry is installed and before any plugin starts (whiteboard G3: off-by-default on a first visit)
  async boot({ beforeActivate } = {}) {
    if (this.#booted) return
    this.#booted = true
    // The WHOLE boot — registry load AND the first activation — is queued on
    // the lifecycle chain here, SYNCHRONOUSLY, before the first await. A
    // restart() triggered while the registry is still being fetched (the '/'
    // -> last-view redirect does exactly that) would otherwise run first,
    // activate everything against an empty runtime map, and then boot's own
    // activation would activate everything a SECOND time without deactivating.
    this.#lifecycle = this.#lifecycle.then(() => this.#doBoot(beforeActivate))
    await this.#lifecycle
    this.#notify()
  }

  async #doBoot(beforeActivate) {
    try {
      const url = import.meta.env.VITE_REGISTRY_URL ?? '/registry.json'
      const res = await fetch(`${url}?t=${Date.now()}`)
      if (!res.ok) throw new Error(`registry fetch failed: ${res.status}`)
      this.registry = (await res.json()).plugins ?? []
    } catch (err) {
      console.error('[omni] could not load plugin registry', err)
      this.registryError = String(err)
      this.registry = []
    }

    const stale = staleIds(Object.keys(this.store.all()), this.registry, this.registryError)
    if (stale.length) {
      console.info('[omni] removed plugins the registry no longer has:', stale)
      for (const id of stale) this.store.uninstall(id)
    }

    // built-ins install themselves through the normal dependency-closure
    // path — the core PACK is the builtin; installing it pulls in every
    // member. A browser that already knows the pack skips this.
    const registryById = new Map(this.registry.map((m) => [m.id, m]))
    for (const manifest of this.registry.filter((m) => m.builtin)) {
      if (this.store.get(manifest.id)) continue
      const installedIds = new Set(Object.keys(this.store.all()))
      const { toInstall, errors } = resolveInstallClosure(
        manifest.id,
        registryById,
        installedIds
      )
      if (errors.length) {
        console.error(`[omni] cannot seed "${manifest.id}":`, errors)
        continue
      }
      for (const m of toInstall) this.store.install(m, { enabled: true })
    }

    // the registry is the source of truth for manifest CONTENT (urls,
    // versions, deps); the store only owns installed/enabled state
    for (const manifest of this.registry) {
      if (this.store.get(manifest.id)) {
        this.store.updateManifest(manifest)
      }
    }

    // heal missing dependencies: a manifest update may have ADDED a
    // dependency (e.g. a pack gained a member) that this browser never
    // installed — install it from the registry instead of blocking
    for (const entry of Object.values(this.store.all())) {
      if (!entry.enabled) continue
      for (const depId of Object.keys(entry.manifest.dependencies ?? {})) {
        if (this.store.get(depId) || !registryById.has(depId)) continue
        const installedIds = new Set(Object.keys(this.store.all()))
        const { toInstall, errors } = resolveInstallClosure(
          depId,
          registryById,
          installedIds
        )
        if (errors.length) {
          console.error(`[omni] cannot heal dep "${depId}":`, errors)
          continue
        }
        for (const m of toInstall) this.store.install(m, { enabled: true })
      }
    }

    try {
      beforeActivate?.(this.store)
    } catch (err) {
      console.error('[omni] beforeActivate failed', err)
    }
    await this.#activateEnabled()
  }

  // True once the first activation pass has STARTED. The shell uses it to tell
  // "nothing is bound yet, boot will bind it" from "bound to the wrong view,
  // needs a restart".
  #activationStarted = false
  hasActivated() {
    return this.#activationStarted
  }

  // Enabled plugins that wait for first use (activation 'lazy') or for a free moment after boot (activation 'idle'), not started yet.
  dormant() {
    return [...this.#dormant.values()]
  }

  // First use of a dormant plugin: start it and every dormant plugin it needs,
  // layer by layer. Queued on the lifecycle chain like restart(). The id stays
  // in #woken, so a restart (a view switch) starts it at once again.
  // Resolves true when the plugin is active.
  wake(id) {
    return this.#wakeAll([id]).then(() => this.status(id) === 'active')
  }

  // Start dormant plugins and every dormant plugin they need, layer by layer, in one go.
  #wakeAll(ids) {
    this.#lifecycle = this.#lifecycle.then(async () => {
      const asleep = ids.filter((id) => this.#dormant.has(id))
      if (!asleep.length) return
      for (const id of asleep) this.#woken.add(id)
      const enabled = this.store.enabledManifests()
      const need = needsOf(asleep, enabled)
      const starting = [...this.#dormant.values()].filter((m) => need.has(m.id))
      for (const m of starting) this.#dormant.delete(m.id)
      for (const layer of topoLayers(starting)) {
        await Promise.all(layer.map((m) => this.#activateOne(m, enabled)))
      }
      this.#notify()
    })
    return this.#lifecycle
  }

  // A plugin may ask the kernel to hold back the next plugin starts (whiteboard G4, 2026-10-20: a visit's card
  // waited 340-775 ms behind plugin starts after a reload). One holder; the hold is capped at 1.5 s.
  holdStarts(fn) {
    return this.#hold.set(fn)
  }

  async #activateEnabled() {
    this.#activationStarted = true
    const input = firstInput() // a key press during boot counts too
    const enabled = this.store.enabledManifests()
    const enabledIds = new Set(enabled.map((m) => m.id))

    const cycle = findCycle(enabled)
    if (cycle) {
      console.error(`[omni] dependency cycle: ${cycle.join(' → ')}`)
    }

    // mark blocked: hard deps that are not installed+enabled (or invalid api)
    const startable = []
    for (const manifest of enabled) {
      const problems = []
      if (!isPack(manifest)) {
        if (!manifest.entry) problems.push('manifest has no entry')
        if (
          manifest.apiVersion &&
          !satisfies(API_VERSION, manifest.apiVersion)
        ) {
          problems.push(
            `needs API ${manifest.apiVersion}, host has ${API_VERSION}`
          )
        }
      }
      for (const depId of Object.keys(manifest.dependencies ?? {})) {
        // the host supplies some ids itself; they are never plugins and are
        // always there, so they can never be missing
        if (HOST_PROVIDED.has(depId)) continue
        if (!enabledIds.has(depId)) {
          const installed = this.store.get(depId)
          problems.push(
            installed
              ? `requires "${depId}" (disabled)`
              : `requires "${depId}" (not installed)`
          )
        }
      }
      if (cycle?.includes(manifest.id)) {
        problems.push(`dependency cycle: ${cycle.join(' → ')}`)
      }
      if (problems.length) {
        this.#setRuntime(manifest.id, {
          status: 'blocked',
          reason: problems.join('; '),
        })
      } else {
        startable.push(manifest)
      }
    }
    this.#propagateBlocked(enabled)

    const eager = eagerIds(enabled, this.#woken)
    this.#dormant = new Map(startable.filter((m) => !eager.has(m.id)).map((m) => [m.id, m]))

    // activate layer by layer so declared deps are ready before dependents
    for (const layer of topoLayers(startable.filter((m) => eager.has(m.id)))) {
      await Promise.all(layer.map((manifest) => this.#activateOne(manifest, enabled)))
    }
    this.#startIdle(input).catch((err) => console.error('[omni] idle plugin starts failed', err))
  }

  // Plugins marked activation 'idle' start after boot, 8 per free moment (IDLE_BATCH); after the first key or pointer
  // press the rest start without waiting (whiteboard G4, S19). One 'plugin idle start' row each says why it started then.
  async #startIdle(input) {
    const idle = topoLayers([...this.#dormant.values()].filter((m) => m.activation === 'idle')).flat()
    if (!idle.length) return
    const idleIds = () => idle.filter((m) => this.#dormant.has(m.id)).map((m) => m.id)
    let why = null
    while (idleIds().length) {
      const began = performance.now()
      if (why !== 'input') why = await Promise.race([whenIdle(), input])
      const left = idleIds()
      const batch = why === 'input' ? left : left.slice(0, IDLE_BATCH)
      const waitedMs = Math.round(performance.now() - began)
      for (const id of batch) this.#trace('plugin idle start', { id, why, waitedMs })
      await this.#wakeAll(batch)
    }
  }

  // a plugin whose (transitive) dependency is blocked/errored is blocked too
  #propagateBlocked(enabledManifests) {
    const byId = new Map(enabledManifests.map((m) => [m.id, m]))
    let changed = true
    while (changed) {
      changed = false
      for (const m of enabledManifests) {
        if (['blocked', 'error'].includes(this.status(m.id))) continue
        for (const depId of Object.keys(m.dependencies ?? {})) {
          const depStatus = this.status(depId)
          if (byId.has(depId) && ['blocked', 'error'].includes(depStatus)) {
            this.#setRuntime(m.id, {
              status: 'blocked',
              reason: `requires "${depId}" (${depStatus})`,
            })
            changed = true
            break
          }
        }
      }
    }
  }

  #setRuntime(id, data) {
    this.runtime.set(id, { ...this.runtime.get(id), ...data })
    this.#notify()
  }

  // A row in the dev log (channel 'kernel'); before omni.dev-logs is up it waits in #early, with its own time.
  #trace(stage, data) {
    const log = this.services.getNow('omni.dev-logs')
    if (log) log.channel('kernel').trace(stage, data)
    else if (this.#early.length < 300) this.#early.push([stage, { ...data, doneAt: new Date().toISOString().slice(11, 23) }])
  }

  async #activateOne(manifest, enabledManifests) {
    await this.#hold.wait()
    // Without this guard a second activation pass creates two copies of
    // every listener/store/service; the feature cannot be switched off
    // because the switch reaches only the last-registered copy.
    const existing = this.runtime.get(manifest.id)
    if (existing?.status === 'loading' || existing?.status === 'active') {
      return
    }

    // a dependency may have failed while this layer was being reached
    for (const depId of Object.keys(manifest.dependencies ?? {})) {
      if (['blocked', 'error'].includes(this.status(depId))) {
        this.#setRuntime(manifest.id, {
          status: 'blocked',
          reason: `requires "${depId}" (${this.status(depId)})`,
        })
        return
      }
    }

    if (isPack(manifest)) {
      // packs have no code; installed+enabled is all they are
      this.#setRuntime(manifest.id, { status: 'active' })
      return
    }

    this.#setRuntime(manifest.id, { status: 'loading' })
    const began = performance.now()
    let loadMs = null
    try {
      const plugin = await waitPatiently(loadPluginModule(manifest.entry), {
        stepMs: LOAD_STEP_MS,
        steps: LOAD_STEPS,
        message: `loading "${manifest.id}" timed out`,
        onSlow: (waitedMs) => this.#trace('plugin load slow', { id: manifest.id, waitedMs }),
      })
      loadMs = Math.round(performance.now() - began)
      if (typeof plugin?.activate !== 'function') {
        throw new Error(`"${manifest.id}" does not export { activate }`)
      }
      const styleEls = injectStyles(manifest)
      const ctxHandle = createPluginContext(manifest, this)
      // registered BEFORE activate so an interrupting deactivation can
      // always dispose whatever the plugin managed to set up
      this.#setRuntime(manifest.id, {
        status: 'loading',
        plugin,
        ctxHandle,
        styleEls,
      })
      await this.#hold.wait()
      // A tab visit that waits for the main thread runs here, not after every plugin has started (G4).
      await yieldToTasks()
      const startedAt = performance.now()
      // syncMs: the part of activate() before its first await, the part that blocks a waiting visit (G4, S15).
      const starting = plugin.activate(ctxHandle.ctx)
      const syncMs = Math.round(performance.now() - startedAt)
      await withTimeout(
        Promise.resolve(starting),
        ACTIVATE_TIMEOUT_MS,
        `activate() of "${manifest.id}" timed out`
      )
      this.#setRuntime(manifest.id, { status: 'active' })
      this.#trace('plugin started', { id: manifest.id, loadMs, syncMs, activateMs: Math.round(performance.now() - startedAt) })
    } catch (err) {
      console.error(`[omni] plugin "${manifest.id}" failed`, err)
      this.#trace('plugin failed', { id: manifest.id, reason: String(err), loadMs, waitedMs: Math.round(performance.now() - began) })
      this.#setRuntime(manifest.id, { status: 'error', reason: String(err) })
      this.#propagateBlocked(enabledManifests ?? this.store.enabledManifests())
    }
  }

  async #deactivateOne(id, { restarting = false } = {}) {
    const rt = this.runtime.get(id)
    // dispose whatever exists — including plugins caught mid-activation
    if (rt?.plugin || rt?.ctxHandle) {
      try {
        await rt.plugin?.deactivate?.()
      } catch (err) {
        console.error(`[omni] deactivate of "${id}" failed`, err)
      }
      rt.ctxHandle?.dispose({ restarting })
      for (const el of rt.styleEls ?? []) el.remove()
    }
    this.runtime.delete(id)
    this.#notify()
  }

  // ---- Manager operations (called by the listing page) ----

  // Everything transitively depending on id among installed+enabled plugins.
  dependentsOf(id) {
    return [...transitiveDependents(id, this.store.enabledManifests())]
  }

  async installMany(manifests) {
    for (const m of manifests) this.store.install(m)
    await this.restart()
  }

  async setEnabled(id, enabled, { cascade = [] } = {}) {
    this.store.setEnabled(id, enabled)
    if (!enabled) {
      for (const depId of cascade) {
        this.store.setEnabled(depId, false, `cascade:${id}`)
      }
    } else {
      // offer-to-restore is done by the UI; auto-restore direct cascades
      for (const [otherId, entry] of Object.entries(this.store.all())) {
        if (entry.disabledReason === `cascade:${id}`) {
          this.store.setEnabled(otherId, true)
        }
      }
    }
    await this.restart()
  }

  async uninstall(id, { cascade = [] } = {}) {
    this.store.uninstall(id)
    for (const depId of cascade) this.store.uninstall(depId)
    await this.restart()
  }

  // Deactivate everything and re-run activation from the store. Simple and
  // correct; per-plugin hot enable/disable can come later if it matters.
  // Lifecycle operations are SERIALIZED: a restart during a restart (fast
  // view switching) queues instead of interleaving — overlap would leak the
  // in-flight activation's registrations (e.g. duplicate toolbar buttons).
  #lifecycle = Promise.resolve()
  restart() {
    this.#lifecycle = this.#lifecycle.then(() => this.#doRestart())
    return this.#lifecycle
  }

  async #doRestart() {
    // a plugin that starts again right after may keep costly things (a loaded model) through the restart
    const staying = new Set(this.store.enabledManifests().map((m) => m.id))
    const ids = [...this.runtime.keys()]
    for (const id of ids.reverse()) {
      await this.#deactivateOne(id, { restarting: staying.has(id) })
    }
    await this.#activateEnabled()
    this.#notify()
  }
}

function withTimeout(promise, ms, message) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms)
    promise.then(
      (v) => {
        clearTimeout(timer)
        resolve(v)
      },
      (e) => {
        clearTimeout(timer)
        reject(e)
      }
    )
  })
}

function injectStyles(manifest) {
  const els = []
  const base = manifest.entry.url
  for (const href of manifest.styles ?? []) {
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = new URL(href, base).href
    link.dataset.omniPlugin = manifest.id
    document.head.appendChild(link)
    els.push(link)
  }
  return els
}

export const kernel = new Kernel()
if (import.meta.env.DEV) {
  window.__omni = kernel
}
